import type {
  BookingSearchParams,
  VehicleType,
} from "@/lib/booking/search-params";
import { bookingSearchParamsToVehiclePayload } from "@/lib/booking/search-params";
import { vehiclesService } from "@/lib/services/vehicles-service";
import type { Vehicle } from "@/lib/types/vehicle";

export type { Vehicle, VehicleRates, VehiclesApiResponse } from "@/lib/types/vehicle";

export function formatAud(amount: number): string {
  return amount.toLocaleString("en-AU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

const DELIVERY_KEYWORDS = [
  "hiace",
  "ute",
  "truck",
  "transit",
  "sprinter",
  "renault master",
  "light truck",
  "heavy truck",
  "1t ",
  "2t ",
];

export async function getVehiclesAsync(
  searchParams: BookingSearchParams,
): Promise<Vehicle[]> {
  const payload = bookingSearchParamsToVehiclePayload(searchParams);
  const response = await vehiclesService.fetchVehicles(payload);

  if (!response.success) {
    throw new Error(response.message);
  }

  return response.data;
}

export function sanitizeImageUrl(url: string | null | undefined): string {
  if (!url) {
    return "";
  }

  return url.replace(/\s+/g, "").trim();
}

export function getVehicleTypeName(vehicle: Vehicle): string {
  return (
    vehicle.type_name?.trim() ||
    vehicle.similar_vehicle_name?.trim() ||
    "Vehicle"
  );
}

export function getFuelTypeLabel(
  fuelTypeId: string | null | undefined,
  fuelTypeMap?: Map<string, string>,
): string {
  if (!fuelTypeId) {
    return "—";
  }

  return fuelTypeMap?.get(fuelTypeId) ?? "—";
}

export function getTransmissionLabel(
  transmissionTypeId: string | null | undefined,
  transmissionTypeMap?: Map<string, string>,
): string {
  if (!transmissionTypeId) {
    return "—";
  }

  return transmissionTypeMap?.get(transmissionTypeId) ?? "—";
}

export function estimateLuggageBreakdown(passengers: number): {
  suitcases: number;
  bags: number;
} {
  return {
    suitcases: Math.min(2, Math.max(1, Math.floor(passengers / 2))),
    bags: Math.min(2, Math.max(1, Math.ceil(passengers / 2.5))),
  };
}

/** Extract year range from names like "Toyota Hilux 4x2 (2018 - 2021)" */
export function extractYearRange(
  similarVehicleName: string | null,
): string | null {
  if (!similarVehicleName) {
    return null;
  }

  const match = similarVehicleName.match(/\(([^)]+)\)\s*$/);
  if (!match?.[1]) {
    return null;
  }

  const value = match[1].trim();
  if (!/\d{4}/.test(value)) {
    return null;
  }

  return value;
}

export function getVehicleDisplayName(vehicle: Vehicle): string {
  return getVehicleTypeName(vehicle);
}

export function getSimilarModelLabel(vehicle: Vehicle): string {
  if (vehicle.similar_vehicle_name) {
    const yearRange = extractYearRange(vehicle.similar_vehicle_name);
    if (yearRange) {
      return vehicle.similar_vehicle_name
        .replace(/\s*\([^)]+\)\s*$/, "")
        .trim();
    }
    return vehicle.similar_vehicle_name;
  }
  return getVehicleDisplayName(vehicle);
}

/** Rough luggage capacity when API does not provide it */
export function estimateLuggageCapacity(passengers: number): number {
  return Math.max(2, Math.min(6, Math.ceil(passengers / 2)));
}

export function isDeliveryVehicle(vehicle: Vehicle): boolean {
  const name = getVehicleTypeName(vehicle).toLowerCase();
  return DELIVERY_KEYWORDS.some((keyword) => name.includes(keyword));
}

export function filterVehiclesByTab(
  vehicles: Vehicle[],
  tab: VehicleType | null,
): Vehicle[] {
  if (!tab) {
    return vehicles;
  }

  if (tab === "delivery") {
    return vehicles.filter(isDeliveryVehicle);
  }

  return vehicles.filter((vehicle) => !isDeliveryVehicle(vehicle));
}
